/* eslint-disable @typescript-eslint/no-use-before-define */
import { useQuery } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';

import { Link } from 'react-router-dom';
import AppDownloadButtons from '../common/components/AppDownloadButtons';
import { GutterFull } from '../common/components/layout';
import { LoadingCentered } from '../common/components/loading';
import ReviewRating from '../common/components/Rating';
import Layout from '../common/layout';
import ProfileImg from '../common/profile-img';
import Tastes from './components/Tastes';
import { REVIEW_LIST } from './gql/review-list';

export default function ListViewPage() {
  const pageMeta = { title: 'List' };
  return (
    <Layout>
      <Helmet>
        <title>{pageMeta?.title}</title>
      </Helmet>
      <Body />
    </Layout>
  );
}

function Body() {
  const pageMeta = { title: 'Review' };
  const params = useParams<{ id: string }>();

  const result = useQuery(REVIEW_LIST, {
    variables: { id: params?.id },
    fetchPolicy: 'cache-first',
  });

  if (result.error) {
    return (
      <GutterFull>
        <div className='grow text-center'>
          <div className='text-9xl'>🥣</div>
          <div className='center'>
            Oops! There has been an issue. Please try it out later.
          </div>
          <br /><br /><br /><br />
          <div className='py-5'>
            <AppDownloadButtons />
          </div>
        </div>
      </GutterFull>
    );
  }

  if (result.loading) {
    return (
      <GutterFull>
        <LoadingCentered />
      </GutterFull>
    );
  }

  const node = result.data?.review_listsCollection?.edges?.[0]?.node;
  if (!node) {
    return (
      <GutterFull>
        <div className='grow text-center'>
          <div className='text-9xl'>🥣</div>
          <div className='center'>List not found.</div>
        </div>
      </GutterFull>
    );
  }

  if (node) {
    pageMeta.title = `${pageMeta.title}: ${node.title}`;
  }

  const profileUrl = node.profiles?.photo_url;

  // Reviews come through the join table: review_list_reviewsCollection → product_reviews
  const reviews = node.review_list_reviewsCollection?.edges?.map(
    (e: any) => e.node.product_reviews
  ) || [];

  return (
    <>
      <Helmet>
        <title>{pageMeta?.title}</title>
      </Helmet>
      <div className='grow pt-20'>
        <GutterFull noTop>
          <div className='pt-4'>
            <div className='prose'>
              <h2 style={{ marginBottom: '0.6rem' }}>{node.title}</h2>
              <div className='relative z-10 inline-block p-2 pr-3 bg-white rounded-xl shadow-md'>
                <div className='flex flex-row items-center bg-white space-x-3'>
                  <div className='flex-grow-0'>
                    <ProfileImg className='w-8 h-8' url={profileUrl} />
                  </div>
                  <div className='flex-grow'>
                    <div className='flex flex-col'>
                      <div className='flex-grow-0'>
                        <span className='text-gray-500 font-semibold'>
                          {node.profiles?.username}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {node.description ? (
              <div className='prose relative -top-5 ml-3 mt-3 p-3 pt-4 whitespace-pre-wrap text-xl border border-gray-100 rounded-lg shadow-sm'>
                <div>{node.description}</div>
              </div>
            ) : (
              <div className='h-4' />
            )}

            <h4 className='font-bold'>Reviews</h4>
            <div className='h-2' />

            <div className='flex flex-col space-y-2'>
              {reviews.map((review: any) => {
                const firstImg = review.product_review_imagesCollection?.edges?.[0]?.node;
                const thumbUrl = firstImg?.url;

                return (
                  <Link
                    to={`/reviews/${review.id}`}
                    key={review.id}
                    className='bg-base-200 flex p-4 rounded-xl'
                  >
                    <div className='w-24 h-24 rounded-xl overflow-hidden'>
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          className='w-full h-full object-cover'
                          alt='product thumb'
                        />
                      ) : null}
                    </div>
                    <div className='w-3' />
                    <div className='flex flex-1 flex-col'>
                      <div className='flex'>
                        <ReviewRating value={review.rating || 0} />
                      </div>
                      <div className='text-lg font-semibold'>
                        {review.product_name}
                      </div>
                      <div>
                        <Tastes tasteList={review.taste_list} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <br />
            <div className='text-sm opacity-20'>created: {node.created_at}</div>
            <div className='text-sm opacity-20'>updated: {node.updated_at}</div>
            <br />

            <AppDownloadButtons />
          </div>
        </GutterFull>
      </div>
    </>
  );
}
